import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useAuth0 } from "@auth0/auth0-react";
import { useMutation } from "@apollo/client";
import AddCustomPolicyDialog from "./AddCustomPolicyDialog";
import UploadFileDialog from "../../../../components/UploadFileDialog/UploadFileDialog";
import {
  CREATE_TENANT_DOCUMENT,
  uploadFile,
} from "../../../../apis/settingsApis/policy-apis/policyApis";

/**
 * Two-step flow: (1) policy name + map to controls, (2) upload document.
 * Creates tenant document via API then uploads file to it.
 */
function AddCustomPolicyFlow({
  open,
  onClose,
  refetch,
  setSnackbarInfo,
  snackbarTransition,
}) {
  const theme = useTheme();
  const { getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const [createTenantDocument] = useMutation(CREATE_TENANT_DOCUMENT);

  const [step, setStep] = useState(0);
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    if (!open) {
      setStep(0);
      setPayload(null);
    } else if (step === 0) {
      setStep(1);
    }
  }, [open, step]);

  const handleFileUpload = async (file) => {
    if (!payload?.policyName?.trim() || !payload?.selectedControls?.length) {
      setSnackbarInfo?.({
        message: "Policy name and at least one control are required.",
        vertical: "top",
        horizontal: "right",
        Transition: snackbarTransition,
      });
      return;
    }
    const policyName = payload.policyName.trim();
    const controlIds = payload.selectedControls.map((c) => c.controlsId);
    try {
      const { data: createData } = await createTenantDocument({
        variables: {
          display_name: policyName,
          control_ids: controlIds,
        },
      });
      const doc = createData?.createTenantDocument?.data;
      if (!doc?.id) {
        throw new Error(
          createData?.createTenantDocument?.message ?? "Failed to create policy"
        );
      }
      const accessToken = await getAccessTokenSilently();
      const idToken = (await getIdTokenClaims()).__raw;
      await uploadFile(file, String(doc.id), 1, accessToken, idToken, true);
      refetch?.();
      setStep(0);
      setPayload(null);
      onClose?.();
      setSnackbarInfo?.({
        message: `${policyName} uploaded successfully.`,
        vertical: "top",
        horizontal: "right",
        Transition: snackbarTransition,
      });
    } catch (err) {
      setSnackbarInfo?.({
        message: err?.message ?? "Failed to create or upload policy.",
        vertical: "top",
        horizontal: "right",
        Transition: snackbarTransition,
      });
    }
  };

  if (!open) return null;

  return (
    <>
      <AddCustomPolicyDialog
        open={step === 1}
        onClose={onClose}
        onNext={(p) => {
          setPayload(p);
          setStep(2);
        }}
        initialPolicyName={payload?.policyName ?? ""}
        initialSelectedControls={payload?.selectedControls ?? []}
      />

      <UploadFileDialog
        open={step === 2}
        onClose={() => {
          setStep(0);
          setPayload(null);
          onClose?.();
        }}
        onBack={() => setStep(1)}
        onFileUpload={handleFileUpload}
        allowedFileTypes={[".docx", ".pdf"]}
        dialogTitle="Add Custom Policy"
        uploadButtonText="Save"
      />
    </>
  );
}

export default AddCustomPolicyFlow;
